import { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useInView, useReducedMotion, useScroll, useTransform } from "motion/react";
import { ArrowRight } from "@phosphor-icons/react";
import { ImpactNumber, MaskWords, ScrubStatement, useMediaQuery } from "../kit.jsx";
import { PARTNER_LOGOS, endingFor, endingThumb } from "../data/endings.js";
import { findRoute } from "../data/routes.js";
import { AURORA_COUNTRIES, AURORA_REPORTED_COUNTRIES, Flag } from "./flags.jsx";

const EASE_OUT = [0.16, 1, 0.3, 1];
const clamp01 = (value) => Math.min(1, Math.max(0, value));

function Action({ action }) {
  if (!action) return null;
  const [label, to] = action;
  const className = "gh-btn gh-btn--fill";
  const inner = <span className="roll" data-label={label}><span>{label}</span></span>;
  return to.startsWith("mailto:") || to.startsWith("http")
    ? <a className={className} href={to}>{inner}</a>
    : <Link className={className} to={to}>{inner}</Link>;
}

function Head({ ending }) {
  return (
    <header className="pend__head">
      <MaskWords as="p" className="pend__line" text={ending.line} key={ending.line} />
      <Action action={ending.action} />
    </header>
  );
}

/* A photo or clip that opens from a card to the full page width as you scroll. */
function MediaScene({ ending }) {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end 0.9"] });
  const clipPath = useTransform(scrollYProgress, (value) => {
    const t = clamp01(value * 1.25);
    const side = (1 - t) * 14;
    const top = (1 - t) * 8;
    return `inset(${top}% ${side}% 0% ${side}% round 28px)`;
  });
  const scale = useTransform(scrollYProgress, (value) => 1.2 - 0.2 * clamp01(value * 1.25));
  const { media } = ending;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || reduceMotion) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) video.play().catch(() => {});
      else video.pause();
    }, { threshold: 0.25 });
    observer.observe(video);
    return () => observer.disconnect();
  }, [reduceMotion, media?.video]);

  if (!media) return null;
  return (
    <div className="pend__media" ref={ref}>
      <motion.figure className="pend__frame" style={reduceMotion ? undefined : { clipPath }}>
        <motion.div className="pend__zoom" style={reduceMotion ? undefined : { scale }}>
          {media.video ? (
            <video ref={videoRef} poster={media.poster} muted loop playsInline preload="none" aria-label={media.alt}>
              {!reduceMotion && <source src={media.video} type="video/mp4" />}
            </video>
          ) : (
            <img src={media.large ?? media.src} alt={media.alt} loading="lazy" decoding="async" />
          )}
        </motion.div>
        {ending.caption ? <figcaption>{ending.caption}</figcaption> : null}
      </motion.figure>
    </div>
  );
}

/* A big number that counts up, with a strip of photos that rise in beside it. */
function CountScene({ ending }) {
  const reduceMotion = useReducedMotion();
  return (
    <div className="pend__count">
      <div className="pend__number">
        <strong><ImpactNumber value={ending.value} /></strong>
        <span>{ending.label}</span>
      </div>
      <ul className="pend__strip">
        {(ending.photos ?? []).filter(Boolean).map((item, index) => (
          <motion.li
            key={item.src}
            initial={reduceMotion ? false : { clipPath: "inset(100% 0% 0% 0% round 20px)", y: 40 }}
            whileInView={{ clipPath: "inset(0% 0% 0% 0% round 20px)", y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1, delay: 0.15 + index * 0.12, ease: EASE_OUT }}
          >
            <img src={item.src} alt={item.alt} loading="lazy" decoding="async" />
          </motion.li>
        ))}
      </ul>
    </div>
  );
}

function StackCard({ item, index, total, progress, spread }) {
  const mid = (total - 1) / 2;
  const offset = index - mid;
  const x = useTransform(progress, (value) => `${offset * spread * clamp01(value)}%`);
  const rotate = useTransform(progress, (value) => offset * 7 * (1 - clamp01(value)) + (index % 2 ? 2 : -2) * (1 - clamp01(value)));
  const y = useTransform(progress, (value) => `${Math.abs(offset) * 6 * clamp01(value)}%`);
  return (
    <motion.li style={{ x, rotate, y, zIndex: total - Math.abs(Math.round(offset)) }}>
      <img src={item.src} alt={item.alt} loading="lazy" decoding="async" />
    </motion.li>
  );
}

/* Photos start as one pile and fan out into a row as you scroll. */
function StackScene({ ending }) {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const narrow = useMediaQuery("(max-width: 767px)");
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.95", "center 0.55"] });
  const photos = (ending.photos ?? []).filter(Boolean).slice(0, 5);
  if (reduceMotion) {
    return (
      <ul className="pend__stack is-static">
        {photos.map((item) => <li key={item.src}><img src={item.src} alt={item.alt} loading="lazy" /></li>)}
      </ul>
    );
  }
  return (
    <ul className="pend__stack" ref={ref}>
      {photos.map((item, index) => (
        <StackCard key={item.src} item={item} index={index} total={photos.length} progress={scrollYProgress} spread={narrow ? 46 : 104} />
      ))}
    </ul>
  );
}

const SCATTER = [[-160, -90, -14], [120, -140, 10], [-60, 120, 8], [190, 60, -9], [-220, 30, 12], [40, -60, -6], [240, -40, 14], [-120, -130, 6], [80, 140, -12], [-30, 60, 9], [160, 110, -7]];

function LogoCard({ logo, index, progress }) {
  const [sx, sy, sr] = SCATTER[index % SCATTER.length];
  const x = useTransform(progress, (value) => `${sx * (1 - clamp01(value))}%`);
  const y = useTransform(progress, (value) => `${sy * (1 - clamp01(value))}%`);
  const rotate = useTransform(progress, (value) => sr * (1 - clamp01(value)));
  const opacity = useTransform(progress, (value) => 0.25 + 0.75 * clamp01(value * 1.4));
  return (
    <motion.li style={{ x, y, rotate, opacity }}>
      <img src={logo[1]} alt={`${logo[0]} logo`} loading="lazy" decoding="async" />
    </motion.li>
  );
}

/* Partner logos arrive from scattered positions and settle into one tidy row. */
function LogosScene() {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "center 0.6"] });
  return (
    <ul className="pend__logos" ref={ref}>
      {PARTNER_LOGOS.map((logo, index) => (reduceMotion
        ? <li key={logo[0]}><img src={logo[1]} alt={`${logo[0]} logo`} loading="lazy" /></li>
        : <LogoCard key={logo[0]} logo={logo} index={index} progress={scrollYProgress} />))}
    </ul>
  );
}

/* Round flags pop in one after another while the count climbs. */
function FlagsScene() {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const play = reduceMotion || inView;
  return (
    <div className="pend__flags" ref={ref}>
      <ul>
        {AURORA_COUNTRIES.map((country, index) => (
          <motion.li
            key={country.code}
            initial={reduceMotion ? false : { scale: 0, y: 30 }}
            animate={play ? { scale: 1, y: 0 } : { scale: 0, y: 30 }}
            transition={{ duration: 0.6, delay: 0.1 + index * 0.06, ease: EASE_OUT }}
          >
            <Flag code={country.code} shape="round" />
            <span>{country.name}</span>
          </motion.li>
        ))}
        <motion.li
          className="pend__flagmore"
          initial={reduceMotion ? false : { scale: 0, y: 30 }}
          animate={play ? { scale: 1, y: 0 } : { scale: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.1 + AURORA_COUNTRIES.length * 0.06, ease: EASE_OUT }}
        >
          <span aria-hidden="true">+12</span>
          <span>more</span>
        </motion.li>
      </ul>
      <p className="pend__flagnote">
        <strong><ImpactNumber value={`${AURORA_REPORTED_COUNTRIES}+`} /></strong> countries in all.
      </p>
    </div>
  );
}

function UpNext({ path }) {
  const route = findRoute(path);
  const reduceMotion = useReducedMotion();
  if (!route) return null;
  const thumb = endingThumb(path);
  const title = path === "/" ? "Back to the start" : route.title;
  return (
    <motion.div
      className="pend__next"
      initial={reduceMotion ? false : { opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.5 }}
      transition={{ duration: 0.8, ease: EASE_OUT }}
    >
      <Link to={path} className="pend__nextlink">
        <span className="pend__nextlabel">Up next</span>
        <span className="pend__nexttitle">{title}</span>
        {thumb ? <span className="pend__nextthumb"><img src={thumb} alt="" loading="lazy" decoding="async" /></span> : null}
        <span className="pend__nextarrow" aria-hidden="true"><ArrowRight weight="bold" /></span>
      </Link>
    </motion.div>
  );
}

export function PageEnding() {
  const location = useLocation();
  const ending = endingFor(location.pathname);
  if (!ending) return null;
  const scene = {
    media: <MediaScene ending={ending} />,
    count: <CountScene ending={ending} />,
    stack: <StackScene ending={ending} />,
    logos: <LogosScene />,
    flags: <FlagsScene />,
    line: null,
  }[ending.type];
  return (
    <section className={`pend pend--${ending.type}`} aria-label="Closing">
      {ending.type === "line" ? (
        <header className="pend__head pend__head--line">
          <ScrubStatement text={ending.line} />
          <Action action={ending.action} />
        </header>
      ) : (
        <Head ending={ending} />
      )}
      {scene}
      {ending.next ? <UpNext path={ending.next} /> : null}
    </section>
  );
}
